import { Fetch } from '@nzyme/ioc/Fetch.js';
import { defineService } from '@nzyme/ioc/Service.js';
import { Logger } from '@nzyme/logging/Logger.js';
import { createSingleRunner } from '@nzyme/utils/createSingleRunner.js';
import { withTimeout } from '@nzyme/utils/withTimeout.js';
import { AuthSession } from '@sigma/client/auth/AuthSession.js';
import { AuthStore } from '@sigma/client/auth/AuthStore.js';
import type { AuthState } from '@sigma/client/auth/AuthStore.js';
import { createAuthStore } from '@sigma/client/auth/createAuthStore.js';
import { getCognitoOAuthUrl } from '@sigma/common/auth/getCognitoOAuthUrl.js';
import { COGNITO_USER_POOL_CLIENT_ID, COGNITO_USER_POOL_DOMAIN } from '@sigma/common/env.js';

import { parseAuthResponse } from './parseAuthResponse.js';

/**
 * How long the best-effort revoke request may run before sign-out stops waiting for it. Sign-out is a
 * security action the patient must be able to complete on demand - including on a flaky mobile
 * connection - so the local clear can never hang behind Cognito. A few seconds is enough for the round
 * trip to land on a normal connection, and short enough that a stalled request is not felt as a hang.
 */
const REVOKE_TIMEOUT_MS = 3000;

/**
 * Patient implementation of {@link AuthStore}. A patient session lives entirely in the tokens the app
 * holds: the OAuth refresh token is the only thing that can renew it, so a snapshot restored from
 * storage is published as-is and only ever leaves through a rejected renewal or an explicit logout.
 * There is no external session to reconcile against, which is exactly what makes this store much
 * simpler than its staff counterpart.
 */
export const PatientAuthStore = defineService({
    name: 'PatientAuthStore',
    implements: AuthStore,
    deps: {
        session: AuthSession,
        userPoolDomain: COGNITO_USER_POOL_DOMAIN,
        userPoolClientId: COGNITO_USER_POOL_CLIENT_ID,
        logger: Logger,
        fetch: Fetch,
    },
    setup({ session, userPoolDomain, userPoolClientId, logger, fetch }) {
        const refreshAuthRunner = createSingleRunner({ handler: refreshAuth });

        // Nothing can vouch for a patient session beyond the tokens themselves, so the store never
        // quarantines: a restored snapshot is live immediately (otherwise a returning patient would
        // be logged out until the first check came back), and a sign-in or sign-out in another tab
        // takes effect here as soon as the synced storage ref changes.

        return createAuthStore({ session, authenticate, checkAuth, refreshAuth, logout });

        /** Adopts the tokens the patient SMS login flow just obtained. */
        async function authenticate(auth: AuthState) {
            session.setAuth(auth);
            await session.notifySignedIn();
        }

        async function checkAuth() {
            if (!(await session.announceUnlessStale())) {
                return;
            }

            // Renewing needs the refresh token the snapshot carries; without one the session can
            // never come back, so it is ended rather than left to fail on every future check.
            if (!session.auth.value?.refreshToken) {
                logger.debug('No refresh token found');
                return await logout();
            }

            await refreshAuthRunner.execute();
        }

        async function refreshAuth() {
            logger.debug('Refreshing auth...');

            const refreshToken = session.auth.value?.refreshToken;
            if (!refreshToken) {
                return;
            }

            const body = new URLSearchParams({
                grant_type: 'refresh_token',
                client_id: userPoolClientId,
                refresh_token: refreshToken,
            });

            const res = await fetch(`${getCognitoOAuthUrl(userPoolDomain)}/oauth2/token`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body,
            });

            if (!res.ok) {
                // TODO: SIG-126 - Don't logout on network errors (offline/disconnect), just retry later
                return await logout();
            }

            const authNew = await parseAuthResponse(res);
            if (!authNew.refreshToken) {
                // Cognito only returns a refresh token when it rotates one; keeping the previous one
                // is what lets a patient stay signed in across renewals.
                authNew.refreshToken = refreshToken;
            }

            session.setAuth(authNew);
            await session.notifyAuthenticated();
        }

        async function logout() {
            await revokeRefreshToken();
            await session.clear();
        }

        /**
         * Best-effort: Cognito revocation also invalidates the access/id tokens issued from this
         * refresh token, closing the window a stolen-but-not-yet-expired token would otherwise leave
         * open. It must never fail `logout()`, and never block it for more than {@link REVOKE_TIMEOUT_MS}
         * - an offline patient, or Cognito rejecting the request, still has to end up signed out of this
         * device; the refresh token's own 30-day validity (see PatientAuthStack) bounds the exposure if
         * revocation never lands. The request is aborted after {@link REVOKE_TIMEOUT_MS} so a stalled
         * connection cannot hold the patient signed in on this device.
         */
        async function revokeRefreshToken() {
            const refreshToken = session.auth.value?.refreshToken;
            if (!refreshToken) {
                return;
            }

            const body = new URLSearchParams({
                token: refreshToken,
                client_id: userPoolClientId,
            });

            try {
                const res = await withTimeout({
                    operation: signal =>
                        fetch(`${getCognitoOAuthUrl(userPoolDomain)}/oauth2/revoke`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                            body,
                            signal,
                        }),
                    timeoutMs: REVOKE_TIMEOUT_MS,
                });

                if (!res.ok) {
                    // `fetch` resolves on 4xx/5xx, so without this check a refused revocation would
                    // pass unnoticed. Never log the token itself - only the status.
                    logger.warn('Cognito refused to revoke patient refresh token', { status: res.status });
                }
            } catch (error) {
                logger.warn('Failed to revoke patient refresh token', { error });
            }
        }
    },
});
